import type { StaffMemberCreationFailedAction } from '@Application/Facilities/StaffMembers/StaffMembers/Actions/staffMemberCreationFailed';
import type { StaffMemberUpdateFailedAction } from '@Application/Facilities/StaffMembers/StaffMembers/Actions/staffMemberUpdateFailed';
import CreateStaffMember from '@Application/Facilities/StaffMembers/StaffMembers/Commands/CreateStaffMember';
import UpdateStaffMember from '@Application/Facilities/StaffMembers/StaffMembers/Commands/UpdateStaffMember';
import createStaffMember from '@Application/Facilities/StaffMembers/StaffMembers/createStaffMember';
import updateStaffMember from '@Application/Facilities/StaffMembers/StaffMembers/updateStaffMember';
import FetchStaffFunctions from '@Application/Facilities/StaffMembers/StaffFunctions/Queries/FetchStaffFunctions';
import fetchStaffFunctions from '@Application/Facilities/StaffMembers/StaffFunctions/fetchStaffFunctions';
import staffMemberSidebarSignal, {
	closeStaffMemberSidebar,
} from '@UI/Pages/Agencies/StaffMembers/Signals/staffMemberSidebarSignal';
import StaffMemberFormData from '@UI/Pages/Agencies/StaffMembers/Sidebars/StaffMemberSidebar/FormData/StaffMemberFormData';
import useOrganizationFacilities from '@UI/Pages/Agencies/StaffMembers/Hooks/useOrganizationFacilities';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Dropdown, Form, InputText, MultiSelect, Sidebar } from '@verclam/ui-kit';

/**
 * One panel for both creation and edition, told apart by `staffMemberId` on the signal's context.
 * Reads the edited staff member straight from the store rather than through a prop: the page mounts
 * this component once, with no data of its own to pass it.
 *
 * The facility multi-select is ALWAYS rendered, never hidden: `facilityIds` is server-required
 * (`@ArrayNotEmpty`), so hiding it for a caller with a narrower reach would submit an empty array and
 * produce a systematic `400`. Only its OPTIONS vary (the server already scopes what it returns to
 * `facilitiesStore.facilities`) — this component writes no condition of its own.
 *
 * `facilitiesStore.facilities` may never have been loaded by another screen: this sidebar reads it
 * through `useOrganizationFacilities`, which fetches it when missing.
 */
const StaffMemberSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(staffMemberSidebarSignal);
	const staffMembers = useSelector((state) => state.staffMembersStore.staffMembers);
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;

	const { data: staffFunctions } = useStoreQuery({
		selector: (state) => state.staffFunctionsStore.staffFunctions,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchStaffFunctions(new FetchStaffFunctions())),
	});

	const { data: facilities } = useOrganizationFacilities();

	const [formData, setFormData] = React.useState(() => StaffMemberFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const staffMemberId = context?.staffMemberId ?? null;

	const editedStaffMember = React.useMemo(() => {
		if (staffMemberId === null || !staffMembers) {
			return null;
		}

		return staffMembers.findById(staffMemberId);
	}, [staffMemberId, staffMembers]);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		setFormData(
			editedStaffMember !== null
				? StaffMemberFormData.fromView(editedStaffMember)
				: StaffMemberFormData.createEmpty(),
		);
	}, [context, editedStaffMember]);

	const handleFirstNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setFirstName(value));
	}, []);

	const handleLastNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLastName(value));
	}, []);

	const handleJobFunctionChange = React.useCallback((value: string | null) => {
		setFormData((current) => current.setJobFunction(value));
	}, []);

	const handlePhoneNumberChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPhoneNumber(value));
	}, []);

	const handleEmailChange = React.useCallback((value: string) => {
		setFormData((current) => current.setEmail(value));
	}, []);

	const handleFacilityIdsChange = React.useCallback((value: readonly number[]) => {
		setFormData((current) => current.setFacilityIds(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppMain` never mounts this page without a resolved organization.
		if (organizationId === null || formData.jobFunction === null) {
			return;
		}

		const phoneNumber =
			formData.phoneNumber === '' || formData.phoneNumber === null ? null : formData.phoneNumber;
		const email = formData.email === '' || formData.email === null ? null : formData.email;

		setIsSubmitting(true);

		if (staffMemberId === null) {
			dispatch(
				createStaffMember(
					new CreateStaffMember(
						organizationId,
						formData.firstName,
						formData.lastName,
						formData.jobFunction,
						phoneNumber,
						email,
						formData.facilityIds,
					),
				),
			)
				.then(() => closeStaffMemberSidebar())
				.catch((action: StaffMemberCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'STAFF_MEMBER_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateStaffMember(
				new UpdateStaffMember(
					organizationId,
					staffMemberId,
					formData.firstName,
					formData.lastName,
					formData.jobFunction,
					phoneNumber,
					email,
					formData.facilityIds,
				),
			),
		)
			.then(() => closeStaffMemberSidebar())
			.catch((action: StaffMemberUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'STAFF_MEMBER_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, staffMemberId, organizationId, dispatch]);

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeStaffMemberSidebar}
			position="right"
			header={
				staffMemberId === null ? 'Nouveau membre du personnel' : 'Modifier le membre du personnel'
			}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeStaffMemberSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form
				id={formId}
				onSubmit={handleSubmit}
				aria-label={
					staffMemberId === null
						? 'Créer un membre du personnel'
						: 'Modifier un membre du personnel'
				}
			>
				<InputText
					name="firstName"
					label="Prénom"
					required
					value={formData.firstName}
					onChange={handleFirstNameChange}
					errorMessage={formData.getValidationMessage('firstName')}
					isFormFieldValid={formData.isValid('firstName')}
				/>
				<InputText
					name="lastName"
					label="Nom"
					required
					value={formData.lastName}
					onChange={handleLastNameChange}
					errorMessage={formData.getValidationMessage('lastName')}
					isFormFieldValid={formData.isValid('lastName')}
				/>
				<Dropdown<string>
					name="jobFunction"
					label="Fonction"
					required
					options={staffFunctions ?? []}
					optionLabel="label"
					optionValue="value"
					loading={staffFunctions === null}
					value={formData.jobFunction}
					onChange={handleJobFunctionChange}
					errorMessage={formData.getValidationMessage('jobFunction')}
					isFormFieldValid={formData.isValid('jobFunction')}
				/>
				<InputText
					name="phoneNumber"
					label="Téléphone"
					type="tel"
					value={formData.phoneNumber ?? ''}
					onChange={handlePhoneNumberChange}
					errorMessage={formData.getValidationMessage('phoneNumber')}
					isFormFieldValid={formData.isValid('phoneNumber')}
				/>
				<InputText
					name="email"
					label="Courriel"
					type="email"
					value={formData.email ?? ''}
					onChange={handleEmailChange}
					errorMessage={formData.getValidationMessage('email')}
					isFormFieldValid={formData.isValid('email')}
				/>
				<MultiSelect<number>
					name="facilityIds"
					label="Agences"
					required
					options={facilities?.list ?? []}
					optionLabel="name"
					optionValue="id"
					loading={facilities === null}
					value={formData.facilityIds}
					onChange={handleFacilityIdsChange}
					errorMessage={formData.getValidationMessage('facilityIds')}
					isFormFieldValid={formData.isValid('facilityIds')}
				/>
			</Form>
		</Sidebar>
	);
};

export default StaffMemberSidebar;
